import { IconButton, InputAdornment, TextField } from '@mui/material';
import { useDebounce } from '@uidotdev/usehooks';
import { cn } from 'cn';
import {
  type ChangeEvent,
  forwardRef,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Icon } from '@vestigas/ui';

export const EndAdornmentClear = ({
  onClick,
}: {
  readonly onClick: () => void;
}) => (
  <IconButton edge="end" size="medium" onMouseDown={onClick}>
    <Icon metaphor="clear" className="size-5" />
  </IconButton>
);

const defaultStartAdornment = (
  <InputAdornment position="start">
    <Icon metaphor="search" />
  </InputAdornment>
);

type P = {
  readonly autoFocus?: boolean;
  readonly debounceTime?: number;
  readonly disabled?: boolean;
  readonly onChange: (value: string) => void;
  readonly onFocus: () => void;
  readonly placeholder: string;
  readonly startAdornment?: ReactNode;
  readonly testId?: string;
  readonly value: string;
} & ComponentStyling;

export const SearchInput = forwardRef<HTMLInputElement, P>(
  (
    {
      autoFocus = false,
      className,
      debounceTime = 600,
      disabled = false,
      onChange,
      onFocus,
      placeholder = 'Suchen ...',
      startAdornment = defaultStartAdornment,
      style,
      testId,
      value = '',
    }: P,
    forwardedRef,
  ) => {
    const [searchTerm, setSearchTerm] = useState(value);
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTime);

    useEffect(() => {
      setSearchTerm(value);
    }, [value]);

    useEffect(() => {
      if (debouncedSearchTerm !== value) {
        onChange(debouncedSearchTerm);
      }
    }, [debouncedSearchTerm, onChange]);

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;

        setSearchTerm(newValue);
      },
      [searchTerm],
    );

    const handleClear = useCallback(() => {
      setSearchTerm('');
    }, [searchTerm]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
          handleClear();
          event.stopPropagation();
        }
      },
      [handleClear],
    );

    const handleFocus = useCallback(() => {
      onFocus?.();
    }, [onFocus]);

    const hasSearchTerm = Boolean(searchTerm);

    const slotProps = {
      htmlInput: {
        'data-testid': testId,
      },
      input: {
        endAdornment: hasSearchTerm ? (
          <EndAdornmentClear onClick={handleClear} />
        ) : null,
        startAdornment,
      },
    };

    return (
      <TextField
        autoComplete="off"
        autoFocus={autoFocus}
        className={cn('w-full bg-white pl-0', className)}
        disabled={disabled}
        inputRef={forwardedRef}
        placeholder={placeholder}
        size="small"
        slotProps={slotProps}
        style={style}
        value={searchTerm}
        variant="outlined"
        onChange={handleChange}
        onClick={handleFocus}
        onKeyDown={handleKeyDown}
      />
    );
  },
);
